<script setup lang="ts">
import {onMounted, PropType, Ref, ref, useTemplateRef} from "vue";
import {ICellEditorParams} from "ag-grid-community";
import {QSelect, Screen} from "quasar";
import SuggestionProviderGridFieldWidgetUtil from "@/utils/editView/widgets/SuggestionProviderGridFieldWidgetUtil.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import {useInputRestrictions} from "@/composables/useInputRestrictions.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import EntityReferenceData = namaTypes.EntityReferenceData;
import SuggestionProvidedItem = namaTypes.SuggestionProvidedItem;
import FieldMetaData = namaTypes.FieldMetaData;

const props = defineProps({
  params: {
    type: Object as PropType<ICellEditorParams>,
    required: true
  }
});
const fieldValue = props.params.value as string | null | undefined;
const columnMetadata: FieldMetaData = props.params['columnMetadata'];
const fieldUtil = new SuggestionProviderGridFieldWidgetUtil(props.params['presenter'](), columnMetadata, ref(fieldValue), props.params?.rowIndex);
const suggestionWrapperRef = useTemplateRef<HTMLElement>("suggestionWrapperRef");
const suggestionFieldStyle = ClientCache.getFieldStyle(props.params['presenter']().entityType, columnMetadata?.fieldId ?? '');
useInputRestrictions(suggestionWrapperRef, suggestionFieldStyle?.fixedInputLanguage, suggestionFieldStyle?.fixedLetterCase);
fieldUtil.params = props.params;
const formattedValue: Ref<any | null> = fieldUtil.formattedValue();
const loading = ref(false)
const options = ref<EntityReferenceData[]>([]);
const qTextFieldSelect = useTemplateRef<QSelect>("qTextFieldSelect");
const shouldFilter = ref(false);
const popupVisible = ref(false);

function getValue() {
  return props.params.parseValue(fieldUtil.inputFieldValueRef.value);
}

onMounted(() => {
  fieldUtil.onFocus();
  if (qTextFieldSelect.value) {
    setTimeout(() => {
      qTextFieldSelect.value?.focus();
      if (Screen.lt.sm)
        onDownHandler();
      if (props.params?.eventKey?.length === 1) {
        qTextFieldSelect.value?.updateInputValue(props.params?.eventKey);
      }
    })
  }
});

async function onDownHandler(e?: any) {
  shouldFilter.value = true;
  if (!popupVisible.value) {
    qTextFieldSelect.value!.updateInputValue(fieldUtil.inputFieldValueRef.value || '', false);
    qTextFieldSelect.value!.showPopup();
  }
}

function onFilter(val, update) {
  update(async () => {
      if (shouldFilter.value) {
        loading.value = true;
        await fieldUtil.suggest(val, options)
        loading.value = false;
      } else {
        options.value = [];
      }
    },
    newRef => {
      if (val !== '' && options.value.length > 0) {
        setTimeout(() => {
          newRef.setOptionIndex(-1) // reset optionIndex in case there is something selected
          newRef.moveOptionSelection(1, true) // focus the first selectable option and do not update the input-value
        });
      }
    })
}

function onBlur() {
  shouldFilter.value = false;
  props.params?.stopEditing();
}

async function onSelect(value?: SuggestionProvidedItem) {
  await fieldUtil.onSelect(value);
  props.params?.stopEditing();
  AGGridUtils.navigateToNextCellAndStartEditing(props.params?.api);
}

function onEnterKeyDown(event: KeyboardEvent) {
  if (AGGridUtils.isBulkAction(event)) {
    namaUpdateValue(getValue());
    AGGridUtils.dispatchBulkEventFor(qTextFieldSelect)
  } else {
    AGGridUtils.navigateToNextCellAndStartEditing(props.params?.api);
  }
}

function namaUpdateValue(value: string) {
  fieldUtil.fieldValue.value = value;
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'F2') {
    onBlur();
  } else if (event.key === 'Escape') {
    props.params?.api.stopEditing(true);
    props.params.api.setFocusedCell(props.params.rowIndex, props.params.column);
  }
}

defineExpose({getValue, namaUpdateValue});
</script>

<template>
  <div ref="suggestionWrapperRef" class="tw-w-full tw-mb-0 !tw-h-full grid-enum-selector">
    <div class="tw-relative tw-leading-tight tw-h-full tw-w-full">
      <q-select
        use-input hide-selected fill-input dense hide-dropdown-icon options-dense
        @filter="onFilter"
        @keydown.enter="onEnterKeyDown"
        @keydown.down="onDownHandler"
        @dblclick="onDownHandler"
        @popup-hide="popupVisible = false"
        @popup-show="popupVisible = true"
        @keydown="onKeyDown"
        input-debounce="0"
        :model-value="formattedValue"
        :loading="loading"
        :options="options"
        option-label="optionText"
        ref="qTextFieldSelect"
        @input-value="fieldUtil.onPartialUserInput"
        @blur="onBlur"
        @update:model-value="onSelect"
        class="tw-bg-[#F8F9FA] tw-shadow tw-appearance-none dark:tw-bg-[#3E3E3E] tw-h-full tw-min-h-full ag-custom-component-popup"
        input-class="tw-bg-transparent focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-namaFontColor-dark
      dark:tw-placeholder-gray-300"
        popup-content-class="!tw-max-h-[30vh] navbar-search-popup dark:tw-bg-namaBackground-deep-dark dark:tw-text-namaFontColor-dark ag-custom-component-popup"
        options-selected-class="tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-dark">
        <template v-slot:option="scope">
          <q-item v-bind="scope.itemProps">
            <q-item-section>
              <q-item-label>{{ scope.opt.optionDisplayCode }}</q-item-label>
              <q-item-label caption class="dark:tw-text-[rgb(255,255,255,0.5)]" v-if="scope.opt.optionCaption">
                {{ scope.opt.optionCaption }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-select>
    </div>
  </div>
</template>

<style scoped>

</style>
